import { useState } from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { apiGet, getApiError } from '../lib/api';
import { cn } from '../lib/cn';
import { useDisableBrowserAutofill } from '../lib/useDisableBrowserAutofill';
import { Wordmark } from '../components/Logo';
import {
  TodayIcon,
  CatalogueIcon,
  OrdersIcon,
  QuotationsIcon,
  CustomersIcon,
  StockIcon,
  ReserveIcon,
  CashIcon,
  FinanceIcon,
  ManufactureIcon,
  SupplierIcon,
  ShopsIcon,
  UsersIcon,
  CategoriesIcon,
  ProductsIcon,
  SettingsIcon,
  CreditsIcon,
  DeliveryNoteIcon,
  RemittanceIcon,
  BankIcon,
  PettyCashIcon,
  JournalIcon,
  DiscountIcon,
  AuditIcon,
} from '../components/NavIcons';
import type { ComponentType } from 'react';
import type { PermissionKey } from '@kalachand/shared';

interface NavItem {
  to: string;
  label: string;
  Icon: ComponentType<{ size?: number; className?: string }>;
  permission?: PermissionKey;
  anyPermissions?: PermissionKey[];
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    items: [{ to: '/', label: 'Today', Icon: TodayIcon }],
  },
  {
    label: 'Sell',
    items: [
      { to: '/catalogue', label: 'Catalogue', Icon: CatalogueIcon, permission: 'products.view' },
      {
        to: '/orders',
        label: 'Orders',
        Icon: OrdersIcon,
        anyPermissions: ['orders.create', 'orders.view_all_shops'],
      },
      // Back-entering the pre-go-live paper invoice book. Gated on the single
      // permission, which sits in NO role list — so owner and admin see it
      // (they inherit every non-owner-only key) and nobody else does unless an
      // owner grants it to them individually for the backfill. A sales girl
      // never sees this entry. Expected to disappear once the backfill is done
      // and the permission is revoked.
      {
        to: '/orders/legacy-intake',
        label: 'Old paper orders',
        Icon: JournalIcon,
        permission: 'orders.legacy_intake',
      },
      {
        to: '/quotations',
        label: 'Quotations',
        Icon: QuotationsIcon,
        permission: 'quotations.view',
      },
      { to: '/customers', label: 'Customers', Icon: CustomersIcon, permission: 'customers.create' },
      {
        to: '/delivery-notes',
        label: 'Delivery notes',
        Icon: DeliveryNoteIcon,
        permission: 'delivery_notes.create',
      },
      { to: '/stock', label: 'Stock', Icon: StockIcon, permission: 'stock.view' },
      {
        to: '/reservations',
        label: 'Reservations',
        Icon: ReserveIcon,
        anyPermissions: ['orders.create', 'orders.view_all_shops'],
      },
      {
        to: '/cash-close',
        label: 'Cash close',
        Icon: CashIcon,
        anyPermissions: ['cash.close', 'orders.view_all_shops'],
      },
      {
        to: '/cash-handover',
        label: 'Cash handover',
        Icon: RemittanceIcon,
        permission: 'remittances.submit_drawer',
      },
      {
        to: '/my-commission',
        label: 'My commission',
        Icon: FinanceIcon,
        permission: 'finance.view_commission',
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        to: '/operations/manufacturing',
        label: 'Manufacturing',
        Icon: ManufactureIcon,
        permission: 'operations.view',
      },
      {
        to: '/operations/suppliers',
        label: 'Purchase orders',
        Icon: SupplierIcon,
        permission: 'operations.view',
      },
      {
        to: '/operations/imports',
        label: 'Awaiting import',
        Icon: SupplierIcon,
        permission: 'operations.view',
      },
      {
        to: '/operations/delivery',
        label: 'Delivery',
        Icon: DeliveryNoteIcon,
        permission: 'operations.view',
      },
    ],
  },
  {
    label: 'Oversight',
    items: [
      { to: '/finance', label: 'Finance', Icon: FinanceIcon, permission: 'finance.view_dashboard' },
      // The nightly emailed sales sheet, on screen, over any date range.
      { to: '/finance/sales-sheet', label: 'Sales sheet', Icon: FinanceIcon, permission: 'finance.view_dashboard' },
      // Over-cap discount sign-off. Owners act on every request; salespeople see
      // their own here (read-only). Carries a live badge of pending requests.
      {
        to: '/discount-approvals',
        label: 'Discount approvals',
        Icon: DiscountIcon,
        anyPermissions: ['discounts.apply_above_ceiling', 'orders.create'],
      },
      // Driver remittances — operations submits delivery bundles, sales girls submit drawer handovers, finance acknowledges
      {
        to: '/remittances',
        label: 'Remittances',
        Icon: RemittanceIcon,
        anyPermissions: [
          'remittances.submit',
          'remittances.submit_drawer',
          'remittances.acknowledge',
        ],
      },
      // Finance-only
      {
        to: '/finance/payments-pending',
        label: 'Verify Payments',
        Icon: BankIcon,
        permission: 'payments.verify',
      },
      {
        to: '/bank-deposits',
        label: 'Bank deposits',
        Icon: BankIcon,
        permission: 'deposits.create',
      },
      {
        to: '/petty-cash',
        label: 'Petty cash',
        Icon: PettyCashIcon,
        permission: 'petty_cash.create',
      },
      {
        to: '/journal-vouchers',
        label: 'Journal vouchers',
        Icon: JournalIcon,
        permission: 'journal_vouchers.create',
      },
      // Credit notes — owner-only
      {
        to: '/credits',
        label: 'Credit notes',
        Icon: CreditsIcon,
        permission: 'credit_notes.create',
      },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/admin/shops', label: 'Shops', Icon: ShopsIcon, permission: 'shops.manage' },
      { to: '/admin/users', label: 'Users', Icon: UsersIcon, permission: 'users.manage' },
      {
        to: '/admin/categories',
        label: 'Categories',
        Icon: CategoriesIcon,
        permission: 'categories.manage',
      },
      {
        to: '/admin/products',
        label: 'Products',
        Icon: ProductsIcon,
        permission: 'products.create',
      },
      { to: '/admin/devices', label: 'Devices', Icon: ShopsIcon, permission: 'manage_devices' },
      {
        to: '/admin/commissions',
        label: 'Commission barems',
        Icon: FinanceIcon,
        permission: 'commissions.manage',
      },
      { to: '/admin/settings', label: 'Settings', Icon: SettingsIcon, permission: 'shops.manage' },
      { to: '/admin/audit-logs', label: 'Audit Log', Icon: AuditIcon, permission: 'system.view_audit_log' },
    ],
  },
];

// A nav path that is a prefix of another nav path (e.g. "/finance" vs
// "/finance/payments-pending") must match exactly — otherwise the parent link
// stays highlighted while you're on a child page. Leaf paths keep NavLink's
// default prefix match so detail routes (e.g. "/orders/:id") still highlight
// their parent ("/orders").
const ALL_NAV_PATHS = NAV_SECTIONS.flatMap((s) => s.items.map((i) => i.to));
const EXACT_MATCH_PATHS = new Set(
  ALL_NAV_PATHS.filter(
    (p) => p === '/' || ALL_NAV_PATHS.some((other) => other !== p && other.startsWith(`${p}/`)),
  ),
);

interface ShopOption {
  id: string;
  code: string;
  name: string;
  type: 'HQ' | 'SHOP' | 'ONLINE';
}

export function AppShell(): JSX.Element {
  const { user, logout, perms, setActiveShop } = useAuth();
  const navigate = useNavigate();

  useDisableBrowserAutofill();

  // "Currently at" shop switcher — only meaningful for a user assigned to
  // more than one shop (e.g. a branch home shop plus an ONLINE grant). HQ-
  // level users already see everything and pick a shop per-transaction instead.
  const allowedShopIds = user?.allowedShopIds ?? [];
  const showShopSwitcher = allowedShopIds.length > 1;
  const shopsQuery = useQuery({
    queryKey: ['shops'],
    queryFn: () => apiGet<ShopOption[]>('/shops'),
    enabled: showShopSwitcher,
  });
  const switchableShops = (shopsQuery.data ?? []).filter((s) => allowedShopIds.includes(s.id));
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const handleShopSwitch = async (shopId: string): Promise<void> => {
    setSwitching(true);
    setSwitchError(null);
    try {
      await setActiveShop(shopId);
    } catch (err) {
      setSwitchError(getApiError(err).message);
    } finally {
      setSwitching(false);
    }
  };

  // Live badge for pending discount approvals. Owners see the company-wide count;
  // a salesperson sees their own pending requests (the endpoint scopes by role).
  const canSeeApprovals = perms.has('discounts.apply_above_ceiling') || perms.has('orders.create');
  const pendingApprovals = useQuery({
    queryKey: ['discount-approvals-pending-count'],
    queryFn: () => apiGet<{ count: number }>('/discount-approvals/pending-count'),
    enabled: canSeeApprovals,
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  });
  const approvalsBadge = pendingApprovals.data?.count ?? 0;

  // Live badge for the reservations pipeline. The chip shows every active
  // reservation (waiting deposit / waiting stock / ready) and turns green the
  // moment any order's stock has landed and it's ready to deliver.
  // Scoped server-side: a seller sees her own shop, HQ sees all.
  const canSeeReservations = perms.has('orders.create') || perms.has('orders.view_all_shops');
  const reservationsPipeline = useQuery({
    queryKey: ['reservations-pipeline-count'],
    queryFn: () => apiGet<{ total: number; ready: number }>('/reservations/pipeline-count'),
    enabled: canSeeReservations,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const reservationsBadge = reservationsPipeline.data?.total ?? 0;
  const reservationsReady = reservationsPipeline.data?.ready ?? 0;

  // Live badge for the finance verification queue — non-cash payments awaiting
  // verify/reject. Finance-only (mirrors the nav item's permission).
  const canVerifyPayments = perms.has('payments.verify');
  // Keyed under the same 'payments-pending' prefix the queue page invalidates
  // on verify/reject, so the chip drops the moment a payment is actioned.
  const paymentsPending = useQuery({
    queryKey: ['payments-pending', 'count'],
    queryFn: () => apiGet<{ count: number }>('/payments/pending-count'),
    enabled: canVerifyPayments,
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  });
  const paymentsBadge = paymentsPending.data?.count ?? 0;

  const canSee = (item: NavItem): boolean => {
    if (item.permission) return perms.has(item.permission);
    if (item.anyPermissions) return item.anyPermissions.some((p) => perms.has(p));
    return true; // no permission required (e.g. "Today")
  };

  // Pipeline chips per nav path. Accent = action required of the viewer;
  // positive = good news waiting (stock arrived); ink = pipeline still
  // waiting, nothing to act on. Hidden at zero.
  const navBadges: Record<string, { count: number; tone: string }> = {
    '/discount-approvals': { count: approvalsBadge, tone: 'bg-accent' },
    '/reservations': {
      count: reservationsBadge,
      tone: reservationsReady > 0 ? 'bg-positive' : 'bg-ink-3',
    },
    '/finance/payments-pending': { count: paymentsBadge, tone: 'bg-accent' },
  };

  return (
    <div className="h-screen flex bg-paper overflow-hidden">
      {/* Sidebar — static full-height column; only <main> scrolls */}
      <aside className="w-60 shrink-0 bg-surface border-r border-rule flex flex-col h-screen">
        {/* Logo bar — fixed h-16 so it lines up exactly with each page's
            PageScaffold header (same height), reading as one top band. */}
        <Link
          to="/"
          className="h-16 shrink-0 px-6 flex items-center border-b border-rule hover:bg-paper-2 transition"
        >
          <Wordmark size="md" />
        </Link>

        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {NAV_SECTIONS.map((section, sIdx) => {
            const visibleItems = section.items.filter(canSee);
            if (visibleItems.length === 0) return null; // hide empty section headers
            return (
              <div key={section.label ?? `s-${sIdx}`} className={sIdx > 0 ? 'mt-5' : undefined}>
                {section.label && (
                  <div className="px-3 mb-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-ink-3/70">
                    {section.label}
                  </div>
                )}
                <div className="space-y-0.5">
                  {visibleItems.map((item) => {
                    const { Icon } = item;
                    const badge = navBadges[item.to];
                    const label =
                      item.to === '/cash-close' && perms.has('orders.view_all_shops')
                        ? 'Cash in shops'
                        : item.label;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={EXACT_MATCH_PATHS.has(item.to)}
                        className={({ isActive }: { isActive: boolean }) =>
                          cn(
                            'flex items-center gap-3 px-3 py-2 rounded text-sm transition',
                            isActive
                              ? 'bg-accent-soft text-accent font-medium'
                              : 'text-ink-2 hover:bg-paper-2 hover:text-ink',
                          )
                        }
                      >
                        <Icon size={18} />
                        <span>{label}</span>
                        {badge && badge.count > 0 && (
                          <span
                            className={cn(
                              'ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-semibold leading-none',
                              badge.tone,
                            )}
                          >
                            {badge.count}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-rule">
          <div className="px-3 mb-3">
            <div className="text-sm font-medium text-ink">{user?.name}</div>
          </div>
          {showShopSwitcher && (
            <div className="px-3 mb-3">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-medium uppercase tracking-widest text-ink-3/70">
                  Currently at
                </span>
                <select
                  value={user?.activeShopId ?? ''}
                  disabled={switching || switchableShops.length === 0}
                  onChange={(e) => void handleShopSwitch(e.target.value)}
                  className="kc-input text-sm"
                >
                  {switchableShops.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.type === 'ONLINE' ? s.name : `${s.code} · ${s.name}`}
                    </option>
                  ))}
                </select>
              </label>
              {switchError && <p className="mt-1 text-xs text-negative">{switchError}</p>}
            </div>
          )}
          <button
            onClick={async () => {
              await logout();
              navigate('/login', { replace: true });
            }}
            className="w-full text-left px-3 py-2 rounded text-sm text-ink-3 hover:bg-paper-2 hover:text-ink transition"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Main — the application frame. On desktop it does NOT scroll; each page
          renders a <PageScaffold> that pins its own header/footer and owns the
          single scroll region. Below `lg` it relaxes to document scroll so touch
          and on-screen keyboards behave. */}
      <main className="flex-1 min-h-0 flex flex-col overflow-x-hidden overflow-y-auto md:overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}
